/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { useMemo, type FC, type ReactNode } from 'react'
import { Table as RBTable, Spinner } from 'react-bootstrap'
import styled from 'styled-components'

const Empty = styled.td`
  & * {
    font-size: .8rem;
    text-transform: uppercase;
    padding: 10px 0 !important;
    text-align: center;
    justify-content: center;
  }
`

export type Props = {
  /**
   * Title of page
   */
  caption?: string,
  /**
   * Columns of table
   */
  head: {
    key: string,
    placeholder: Partial<ReactNode> & {
      className?: string
    } | any
  }[],
  /**
   * Lines of table
   */
  rows: {
    [key: string]: ReactNode | string | number
  }[],
  /**
   * Rendered when no have data
   */
  emptyView?: ReactNode,
  /**
   * Whether to show the loading state or not
   */
  isLoading?: boolean,
  className?: string
}

/**
 * Table is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import Table from './src/components/Table/Table.tsx'
 *
 * const MyComponent = () => (
 *   <Table />
 * )
 * export default MyComponent
 * ```
 */
const Table: FC<Props> = ({
  caption,
  head = [],
  rows = [],
  emptyView,
  isLoading = false
}) => {
  const headItems = useMemo(() => {
    return (
      <tr>
        {head?.map(({ placeholder, ...otherProps }) =>
          <th {...otherProps} key={placeholder}>
            {placeholder}
          </th>
        )}
      </tr>
    )
  }, [head])

  const body = useMemo(() => {
    if (rows.length === 0 && emptyView) {
      return (
        <tr>
          <Empty colSpan={head.length}>
            {emptyView}
          </Empty>
        </tr>
      )
    }

    return rows?.map((row, index) => (
      <tr key={JSON.stringify(row)}>
        {head.map((item) =>
          <td
            key={item.key}
            className={typeof item.placeholder !== 'string'
              ? item.placeholder.className.toString
              : undefined
            }
          >
            <div>{row[item.key]}</div>
          </td>
        )}
      </tr>
    ))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, emptyView])

  if (isLoading) {
    return (
      <Spinner animation="border">
        <span>Loading...</span>
      </Spinner>
    )
  }

  return (
    <RBTable size='sm'>
      {caption && <caption>{caption}</caption>}
      <thead>{headItems}</thead>
      <tbody>{body}</tbody>
    </RBTable>
  )
}

export default Table
